// Copyright (c) Docugami, Inc. All rights reserved.

import { DisabledCss } from "components/common/DgCommonStyledComponents";
import { dgTagFontFamily, userFontFamily } from "components/common/Fonts";
import { cellBackgroundGray, greyText, lightButtonGray, primaryTheme } from "components/shell/presentation/AppStyles";
import styled, { css } from "styled-components";

export const cardShadow = '0px 0px 5px rgba(0, 0, 0, 0.2)';

const ActiveCss = css`
    padding: 0px;
    border: 2px solid ${lightButtonGray};
    
    ::before {
        display: flex;
        align-items: center;
        justify-content: center;
        content: "Create New Group";
        background-color: ${cellBackgroundGray};
        border-radius: 4px 4px 0px 0px;

        font-family: ${dgTagFontFamily};
        color: black;
        line-height: 12px;
        font-size: 12px;

        height: 25px;
        animation: heightTransition 0.3s ease-in-out;

        @keyframes heightTransition {
            0% {
                color: ${cellBackgroundGray};
                height: 0px;
            }
            100% {
                color: black;
                height: 25px;
            }
        }
    }
`;

export type ItemCardletStyles = {
    root?: string[];
}

export const ActiveCardletDropTargetStyle = "activeDropTarget";
export const DisabledCardletStyle = "disabled";
export const SelectableCardletStyle = "selectable";
export const ActiveEditingCardletStyle = "activeEditTarget";
export const ModalCardletStyle = "modalCardletStyle";

export const CardContainer = styled.div.attrs(props => ({ className: props.className }))`
    background: ${primaryTheme.palette.white};
    border-radius: 5px;
    margin: 5px; // Ensure box shadow isn't cut off
    box-shadow: ${cardShadow};

    // This is a hack to force hardware acceleration on this element,
    // which is a workaround to prevent the react-dnd drag preview from capturing
    // an image of the full overflow size of this element
    transform: translate3d(0, 0, 0);

    &.${ActiveCardletDropTargetStyle} {
        ${ActiveCss}
    }

    &.${DisabledCardletStyle} {
        ${DisabledCss}
    }

    &.${SelectableCardletStyle} {
        & > * {
            pointer-events: none;
        }
        cursor: pointer;
    }

    &.${ActiveEditingCardletStyle} {
        transform: scale(1.01);
        box-sizing: border-box;
    }

    &.${ModalCardletStyle} {
        margin: 0;
    }
`

export const GroupCardContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: 1px solid ${lightButtonGray};
    background: ${cellBackgroundGray};
    border-radius: 5px;
    // Account for report item cardlet margin 
    margin: 5px;
    box-shadow: ${cardShadow};
`;

export const DragWrapper = styled.div<{ isDragging?: boolean }>`
    opacity: ${props => !!props.isDragging ? .5 : 1};
`

export const CardletLabel = styled.span`
    font-family: ${dgTagFontFamily};
    font-weight: bold;
    font-size: 16px;
    color: ${primaryTheme.palette.black};
`

export const CardTextStyle = css`
    font-size: 12px;
    font-family: ${userFontFamily};
    font-weight: 400;
`

export function getClassNames(names?: string | string[]): string | undefined {
    if (!!names) {
        if (typeof names === 'string') {
            return names;
        }
        else if (names.length > 0) {
            return names.join(' ');
        }
    }
}

export const GrabHandleContainer = styled.div<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    user-select: none;
    color: ${greyText};
    :hover {
        cursor: grab;
    }

    ${props => !!props.disabled && DisabledCss};
`